// Argus — windowManager helpers (open/close/bringToFront/persist).
// Persist DB e debounced 500ms per fereastră — drag/resize în continuu nu spamează API.

import {
  upsertLayoutPreference,
  fetchLayoutPreferences,
  type LayoutPreference,
  type PanelType,
} from './layoutPrefs';
import { makeWindowKey, useWindowStore, type WindowState } from '../store/windowStore';

const DEFAULT_SIZES: Record<PanelType, { width: number; height: number }> = {
  fileExplorer: { width: 720, height: 480 },
  desktop: { width: 800, height: 520 },
  webcam: { width: 480, height: 360 },
  mic: { width: 360, height: 200 },
  systemInfo: { width: 520, height: 420 },
  urlMonitoring: { width: 720, height: 480 },
  settings: { width: 520, height: 420 },
  // Bloc task-manager-services 2026-05-07
  taskManager: { width: 900, height: 600 },
  services: { width: 900, height: 600 },
  // Bloc remote-powershell-shell 2026-05-08
  powerShell: { width: 800, height: 500 },
};

const CASCADE_OFFSET = 32;

interface PersistDebouncer {
  timer: number | null;
}
const persistDebouncers = new Map<string, PersistDebouncer>();

// Cache layout DB row pentru reopen restore (păstrează position+size la close).
let layoutCache: Map<string, LayoutPreference> | null = null;

async function ensureLayoutCache(): Promise<Map<string, LayoutPreference>> {
  if (layoutCache !== null) return layoutCache;
  try {
    const items = await fetchLayoutPreferences();
    layoutCache = new Map(items.map((it) => [makeWindowKey(it.agentId, it.panelType), it]));
  } catch (err) {
    console.warn('Layout cache fetch failed', err);
    layoutCache = new Map();
  }
  return layoutCache;
}

export function refreshLayoutCache(items: LayoutPreference[]): void {
  layoutCache = new Map(items.map((it) => [makeWindowKey(it.agentId, it.panelType), it]));
}

export function openPanel(panelType: PanelType, agentId: string): void {
  const store = useWindowStore.getState();
  const key = makeWindowKey(agentId, panelType);

  const existing = store.openWindows.get(key);
  if (existing) {
    // Dacă fereastra e minimized → restore (un-minimize) pentru a apărea la dimensiunea/starea
    // anterioară. Apoi bringToFront. Dacă e deja vizibilă, doar bringToFront.
    if (existing.isMinimized) {
      store.setMinimized(key, false);
    }
    store.bringToFront(key);
    void persistWindow(useWindowStore.getState().openWindows.get(key)!);
    return;
  }

  // Restore last known rect din cache DB dacă există (chiar dacă isOpen=false la close).
  const cached = layoutCache?.get(key);
  const newZ = store.topZ + 1;
  let rect: { x: number; y: number; width: number; height: number };
  if (cached) {
    rect = { x: cached.x, y: cached.y, width: cached.width, height: cached.height };
  } else {
    const cascadeIndex = store.openWindows.size;
    const size = DEFAULT_SIZES[panelType];
    rect = {
      x: 80 + cascadeIndex * CASCADE_OFFSET,
      y: 60 + cascadeIndex * CASCADE_OFFSET,
      width: size.width,
      height: size.height,
    };
  }

  const next: WindowState = {
    key,
    agentId,
    panelType,
    ...rect,
    zOrder: newZ,
    isMaximized: false,
    isMinimized: false,
  };

  store.upsertWindow(next);
  void persistWindow(next);
  // Lazy populate cache la prima deschidere.
  if (!cached) void ensureLayoutCache();
}

export function closePanel(key: string): void {
  const store = useWindowStore.getState();
  const win = store.openWindows.get(key);
  store.removeWindow(key);
  cancelDebounce(key);
  if (win) {
    // NU delete: păstrăm row cu isOpen=false ca să restoreăm position+size la reopen.
    // Reset isMaximized/isMinimized la close (la reopen — fereastra e draggable normal).
    void upsertLayoutPreference({
      agentId: win.agentId,
      panelType: win.panelType,
      x: Math.round(win.x),
      y: Math.round(win.y),
      width: Math.round(win.width),
      height: Math.round(win.height),
      zOrder: win.zOrder,
      isOpen: false,
      isMaximized: false,
      isMinimized: false,
    }).then(() => {
      if (layoutCache) {
        layoutCache.set(key, {
          agentId: win.agentId,
          panelType: win.panelType,
          x: Math.round(win.x),
          y: Math.round(win.y),
          width: Math.round(win.width),
          height: Math.round(win.height),
          zOrder: win.zOrder,
          isOpen: false,
          isMaximized: false,
          isMinimized: false,
          updatedAt: new Date().toISOString(),
        });
      }
    }).catch((err) => {
      console.warn('Layout close persist failed', key, err);
    });
  }
}

export function bringToFront(key: string): void {
  const store = useWindowStore.getState();
  const before = store.openWindows.get(key);
  if (!before) return;
  store.bringToFront(key);
  const after = useWindowStore.getState().openWindows.get(key);
  if (after && after.zOrder !== before.zOrder) {
    schedulePersist(after);
  }
}

export function toggleMaximize(key: string): void {
  const store = useWindowStore.getState();
  const win = store.openWindows.get(key);
  if (!win) return;
  store.setMaximized(key, !win.isMaximized);
  store.bringToFront(key);
  const after = useWindowStore.getState().openWindows.get(key);
  if (after) void persistWindow(after);
}

export function minimizeWindow(key: string): void {
  const store = useWindowStore.getState();
  const win = store.openWindows.get(key);
  if (!win) return;
  store.setMinimized(key, true);
  const after = useWindowStore.getState().openWindows.get(key);
  if (after) void persistWindow(after);
}

export function restoreWindow(key: string): void {
  const store = useWindowStore.getState();
  const win = store.openWindows.get(key);
  if (!win) return;
  store.setMinimized(key, false);
  store.bringToFront(key);
  const after = useWindowStore.getState().openWindows.get(key);
  if (after) void persistWindow(after);
}

const MIN_DIM = { width: 280, height: 200 };

function constrainRect(rect: { x: number; y: number; width: number; height: number }): { x: number; y: number; width: number; height: number } {
  const canvas = document.querySelector('.argus-dashboard-canvas') as HTMLElement | null;
  if (!canvas) return rect;
  const cw = canvas.clientWidth;
  const ch = canvas.clientHeight;
  if (cw === 0 || ch === 0) return rect;
  const width = Math.max(MIN_DIM.width, Math.min(rect.width, cw));
  const height = Math.max(MIN_DIM.height, Math.min(rect.height, ch));
  const x = Math.max(0, Math.min(rect.x, cw - width));
  const y = Math.max(0, Math.min(rect.y, ch - height));
  return { x, y, width, height };
}

export function updateRect(
  key: string,
  rect: { x: number; y: number; width: number; height: number }
): void {
  const store = useWindowStore.getState();
  const constrained = constrainRect(rect);
  store.updateRect(key, constrained);
  const updated = useWindowStore.getState().openWindows.get(key);
  if (updated) {
    schedulePersist(updated);
  }
}

function schedulePersist(win: WindowState): void {
  const existing = persistDebouncers.get(win.key);
  if (existing?.timer !== null && existing?.timer !== undefined) {
    clearTimeout(existing.timer);
  }
  const timer = window.setTimeout(() => {
    persistDebouncers.delete(win.key);
    void persistWindow(win);
  }, 500);
  persistDebouncers.set(win.key, { timer });
}

function cancelDebounce(key: string): void {
  const existing = persistDebouncers.get(key);
  if (existing?.timer !== null && existing?.timer !== undefined) {
    clearTimeout(existing.timer);
  }
  persistDebouncers.delete(key);
}

async function persistWindow(win: WindowState): Promise<void> {
  try {
    await upsertLayoutPreference({
      agentId: win.agentId,
      panelType: win.panelType,
      // Rect natural (NU rect maximized; x/y/w/h în store NU se schimbă la maximize).
      x: Math.round(win.x),
      y: Math.round(win.y),
      width: Math.round(win.width),
      height: Math.round(win.height),
      zOrder: win.zOrder,
      isOpen: true,
      isMaximized: win.isMaximized,
      isMinimized: win.isMinimized,
    });
  } catch (err) {
    console.warn('Layout upsert failed', win.key, err);
  }
}
